import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../api/client'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { awaitsProof, errorCode, invalidateIssueQueries, proofLabel } from './issueFamily'

type ClosableIssue = {
    uid: string
    wait_for?: unknown
    proof?: unknown
    children_summary?: { open: number; in_progress: number; draft: number } | null
}

/** Open children the caller can see on the ticket. */
export function openChildrenCount(issue: ClosableIssue): number {
    const s = issue.children_summary
    return s ? s.open + s.in_progress + s.draft : 0
}

/** Whether closing this ticket needs the dialog rather than a plain PATCH. */
export function closeNeedsDialog(issue: ClosableIssue): boolean {
    return openChildrenCount(issue) > 0 || awaitsProof(issue)
}

/**
 * Closing a parent with open sub-tickets, or a step that closes on its proof
 * (spec 2026-10-27-19 §§ 2, 2b): the two explicit modes of the rollup, and the
 * reason a manual close needs.
 */
export function CloseIssueDialog({ orgCode, issue, open, onOpenChange }: {
    orgCode: string
    issue: ClosableIssue
    open: boolean
    onOpenChange: (open: boolean) => void
}) {
    const { t } = useTranslation('issues')
    const queryClient = useQueryClient()
    const [mode, setMode] = useState<'close' | 'keep_open'>('close')
    const [reason, setReason] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // The server may know of open children the caller cannot read.
    const [serverChildren, setServerChildren] = useState(0)

    useEffect(() => {
        if (!open) return
        setMode('close')
        setReason('')
        setError(null)
        setServerChildren(0)
    }, [open])

    const childCount = Math.max(openChildrenCount(issue), serverChildren)
    const proof = awaitsProof(issue)
    const needsReason = proof || (childCount > 0 && mode === 'keep_open')
    const canConfirm = !busy && (!needsReason || reason.trim() !== '')

    const confirm = async () => {
        setBusy(true)
        setError(null)
        const body: Record<string, unknown> = { status: 'closed' }
        if (childCount > 0) body.children = mode
        if (needsReason) body.close_reason = reason.trim()
        const { error: err } = await apiClient.PATCH('/api/v1/organizations/{orgCode}/issues/{issueUid}', {
            params: { path: { orgCode, issueUid: issue.uid } },
            body: body as never,
        })
        setBusy(false)
        if (err) {
            const code = errorCode(err)
            if (code === 'ISSUE_HAS_OPEN_CHILDREN') {
                const e = err as { open_children?: unknown[]; other_open_count?: number }
                setServerChildren((e.open_children?.length ?? 0) + (e.other_open_count ?? 0))
                return
            }
            setError(code ?? t('children.error', { reason: '' }))
            return
        }
        invalidateIssueQueries(queryClient)
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md" data-testid="issue-close-children-dialog">
                <DialogHeader>
                    <DialogTitle>{t('close_dialog.title')}</DialogTitle>
                    <DialogDescription>
                        {childCount > 0
                            ? t('close_dialog.children_description', { count: childCount })
                            : t('close_dialog.proof_description', { proof: proofLabel(t, issue.wait_for) })}
                    </DialogDescription>
                </DialogHeader>
                {childCount > 0 && (
                    <RadioGroup value={mode} onValueChange={(v) => setMode(v as 'close' | 'keep_open')} className="gap-2">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <RadioGroupItem value="close" data-testid="issue-close-children-close" />
                            {t('close_dialog.close_children')}
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <RadioGroupItem value="keep_open" data-testid="issue-close-children-keep-open" />
                            {t('close_dialog.keep_open')}
                        </label>
                    </RadioGroup>
                )}
                {needsReason && (
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground" htmlFor="issue-close-reason">{t('close_dialog.reason')}</label>
                        <Textarea
                            id="issue-close-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={t('close_dialog.reason_placeholder')}
                            data-testid="issue-close-reason"
                        />
                    </div>
                )}
                {error && <p className="text-sm text-destructive" data-testid="issue-close-error">{error}</p>}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="issue-close-cancel">{t('close_dialog.cancel')}</Button>
                    <Button onClick={confirm} disabled={!canConfirm} data-testid="issue-close-confirm">{t('close_dialog.confirm')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
